import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleMapsModule } from '@angular/google-maps';
import { ToastrService } from 'ngx-toastr';

import { ProjectService } from '../services/project.service';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { ProposalService } from '../services/proposal.service';
import { GoogleMapsLoaderService } from '../services/google-maps-loader.service';
import {
  ProductVm,
  SiteVm,
  buildProductTitle,
  sanitizeProjectDetails,
  toProjectVm,
} from '../list/list-grouping.util';
import { SITE_VISIT_SCHEDULED_STATUSES } from '../list/product-line/product-progress.util';
import type { SiteVisitStatus } from '../site-visit/site-visit-state.util';
import { buildTestId, createTestIdScope } from '../shared/testid/testid.util';

import { StatusPillComponent } from '../shared/status-pill/status-pill.component';
import { DocumentsCardComponent } from './documents-card/documents-card.component';
import { SchedulingCardComponent } from './scheduling-card/scheduling-card.component';
import { CommentsCardComponent } from './comments-card/comments-card.component';
import { SubmitForReviewDialogComponent } from '../submit-for-review-dialog/submit-for-review-dialog.component';

// A resolved contact for a mailto row (PM / staffing coordinator). `null` name
// degrades to "Not assigned"; `null` email renders the name as plain text.
interface WorkspaceContact {
  name: string | null;
  email: string | null;
}

// One row of the ALTA/survey Action Items checklist — `done` gates the
// green-check/red-outstanding icon, `date` (when known) renders "Completed
// {date}".
interface ActionItemState {
  done: boolean;
  date: Date | null;
}

// Status-banner state — mirrors connect-ws `deriveSiteStatus` rebranded to
// Spec-2's semantic triads. Computed per product (survey noun vs deliverable
// noun differ). The dedicated 'in-review' state covers a product that has been
// submitted (past ACTIVE) but is not yet COMPLETED.
interface AltaStatus {
  key:
    | 'completed'
    | 'outstanding-comments'
    | 'past-due'
    | 'due-soon'
    | 'revision-requested'
    | 'on-track'
    | 'in-review';
  triad: 'info' | 'danger' | 'warning' | 'success';
  icon: string;
  label: string;
  message: string;
}

/**
 * A single product line-item's fully-derived view-model — everything one
 * product card in the workspace needs. Built once per emission in
 * `buildProduct()` so the template binds to plain fields (no per-CD-cycle
 * getter recompute) and each card is self-describing. The original
 * `{ site, proposal }` `siteDetail` is kept intact so the child cards
 * (documents/scheduling/comments) and the deliverable-submit flow can pass the
 * untouched objects to `ProposalService`/`DocumentService`.
 */
export interface WorkspaceProduct {
  siteDetail: any;
  site: any;
  proposal: any;
  status: string;
  isAlta: boolean;
  title: string;
  /** Per-product-unique data-testid fragment (deduped product slug). */
  testId: string;

  // Header stepper + status pill (moved out of the shared header into the card).
  stageIndex: number;
  statusPillKey: string;
  statusPillLabel: string;

  // Status banner.
  banner: AltaStatus;
  showBanner: boolean;
  /** True iff the banner resolved to 'past-due' — drives rollup + counts. */
  isPastDue: boolean;

  // Deliverable-submit gating (reproduced verbatim from the single-site path).
  isReviewStage: boolean;
  isRevisionStage: boolean;
  isPendingState: boolean;
  isComplete: boolean;
  pendingLabel: string;
  showReviewSubmission: boolean;
  showRevisionSubmission: boolean;

  // Merged site+proposal document list. Fed to this product's own
  // `scope="product"` documents-card, which only renders the DELIVERABLE/
  // REVISION-typed subset — the SITE/ATP/PROPOSAL/SURVEY_CHECKLIST subset is
  // shared across the site's products and rendered once via the workspace's
  // `sharedDocuments` + a single `scope="shared"` instance (Task 3).
  documents: any[];

  // Report Writer (Quire) — non-survey products only.
  reportWriterUrl: string | null;
  hasReportWriterLink: boolean;

  // Assignment Details (PA).
  dueDate: Date | null;
  committedDate: Date | null;
  reportId: string;
  staffingCoordinator: WorkspaceContact;
  seniorReviewer: WorkspaceContact;

  // Gates BOTH site-visit surfaces on this card: the Details subcard's "Site
  // Visit" row (non-ALTA variant) and the "Field Work Scheduled" Action Item
  // (ALTA variant). Nothing site-visit-related renders for a product that does
  // not require a visit — the same rule `scheduling-card.component.ts` has
  // always applied via `requiringCount`.
  siteVisitRequired: boolean;
  siteVisitSummary: string;

  // Surveyor Information (ALTA/survey).
  authorizationDate: Date | null;
  businessDaysQuoted: number | null;
  surveyDueDate: Date | null;
  titleWorkProvidedDate: Date | null;
  approvedDelayDate: Date | null;
  outstandingCommentsCount: number;

  // Action Items (ALTA/survey).
  fieldWorkItem: ActionItemState;
  prelimSurveyItem: ActionItemState;
  revisedSurveyItem: ActionItemState;
}

// Per-status rollup counts surfaced in the shared header summary line.
interface StatusCounts {
  pastDue: number;
  revisionRequested: number;
  inReview: number;
  active: number;
  complete: number;
}

/**
 * CONNECT 2.0 post-award workspace — the `/active/:id` (and read-only
 * `/project-history/:id`) per-PHYSICAL-SITE cockpit. A single physical site
 * (grouped by `siteNumber`) can carry an ALTA survey AND several ASTM reports,
 * each its own product (a distinct connect `Site` record with its own
 * `proposal`). The route `:id` stays a single representative `site.id`; the
 * workspace resolves that product's project, gathers ALL sibling products
 * sharing the same `siteNumber` (reusing `toProjectVm` → `SiteVm.products`),
 * and renders:
 *
 *  - a shared header once (Back, address, rollup status pill + per-status
 *    counts);
 *  - a shared site-info block once (parcel map + geometry, PM, Table A +
 *    changes, Special Site Condition, Site Details, Site-Visit Instructions) —
 *    these fields are site-level and identical across the site's products;
 *  - shared site/received documents once (SITE, ATP, Project & Review —
 *    Decision #5), via a single `scope="shared"` documents-card;
 *  - a Survey section (0–1 product) and a Reports & Assessments section
 *    (0–N products), each product rendered as its own card with only ITS OWN
 *    deliverable/revision documents (a `scope="product"` documents-card).
 *
 * Per-product writes (deliverable submit, document upload, comment, site visit)
 * merge back into the shared `ProjectService` list keyed by `site.id` without
 * dropping siblings; the local view re-derives the full product collection by
 * `siteNumber` on every emission.
 *
 * `proposal-details` is left untouched — it still serves /project-overview.
 */
@Component({
  standalone: true,
  selector: 'app-workspace',
  imports: [
    CommonModule,
    GoogleMapsModule,
    StatusPillComponent,
    DocumentsCardComponent,
    SchedulingCardComponent,
    CommentsCardComponent,
    SubmitForReviewDialogComponent,
  ],
  templateUrl: './workspace.component.html',
  styleUrl: './workspace.component.scss',
  providers: [DatePipe],
})
export class WorkspaceComponent implements OnInit, OnDestroy {
  private mapsLoadedSub: Subscription;

  // Tears down the route + ProjectService subscriptions on destroy so a
  // navigated-away workspace stops reacting to the app-wide ReplaySubject.
  private readonly destroy$ = new Subject<void>();

  // Route param — a single representative `site.id`. The physical site is
  // resolved from it (its project + `siteNumber`); the param itself never
  // changes on a per-product write, so it remains a stable deep-link.
  id: string = '';

  // The full matched project detail (ALL its sites) — kept intact so a
  // per-product write can rebuild `{ ...matchedProject, sites: [oneSite] }` and
  // let `ProjectService.updateProjectDataSubjectWithSingleSite` merge it by
  // `site.id` into the shared list without dropping siblings.
  private matchedProject: any = null;

  // The physical site's products (survey + reports), fully derived. Empty until
  // the first emission resolves; `representativeSiteDetail` gates the template.
  products: WorkspaceProduct[] = [];

  // Any product's `{ site, proposal }` — the shared site-level fields
  // (address/parcels/Table A/callouts) are identical across the group, so a
  // single representative row feeds the shared header + site-info block. Prefer
  // the deep-linked product (route `:id`), else the first product.
  representativeSiteDetail: any = null;

  siteNumber: number | null = null;

  // The resolved project's id, for the children that need to scope a read of their own to it — the
  // site-visit card filters the PA's cross-project scheduling queue on this plus `siteNumber`. A getter
  // rather than a second field so it cannot drift from `matchedProject`, which `rebuildFromDetails`
  // sets and clears on every emission. Same id space the queue rows carry: both are
  // `projectDetail.projectId` off `ProjectService.fetchData()`.
  get projectId(): string | null {
    return this.matchedProject?.projectId ?? null;
  }

  // Read-only mode (the completed-record variant served at
  // /project-history/:id). Sourced from the route's static `data`; renders every
  // product view-only (no upload/scheduling/submit/reply controls).
  readOnly: boolean = false;

  // --- Shared header / site-info (site-level, rendered once) ---
  address: { streetAddress: string; cityStateZip: string } = {
    streetAddress: '',
    cityStateZip: '',
  };
  siteAcreage: number | null = null;
  projectManager: WorkspaceContact = { name: null, email: null };

  tableAText: string = '';
  // The Item 20/21 free text and the label naming which item it came from.
  tableAItemText: string = '';
  tableAItemLabel: string = '';
  tableAItemsAdded: string = '';
  tableAItemsRemoved: string = '';
  hasTableAChanges: boolean = false;
  specialSiteCondition: string = '';
  siteDetailsText: string = '';
  siteVisitInstructions: string = '';

  // Shared site/received documents (SITE, ATP, Project & Review — Decision
  // #5) — identical across the site's products, so a single representative
  // product's merged document list feeds the ONE `scope="shared"`
  // documents-card. Each product's own card (`scope="product"`) renders only
  // that product's deliverable/revision documents.
  sharedDocuments: any[] = [];

  // Rollup status pill + per-status counts.
  rollupPillKey: string = 'in_progress';
  rollupPillLabel: string = 'In Progress';
  counts: StatusCounts = {
    pastDue: 0,
    revisionRequested: 0,
    inReview: 0,
    active: 0,
    complete: 0,
  };

  // --- Deliverable submit dialog (singleton; per-product context) ---
  submitForReviewActive: boolean = false;
  // The product whose deliverable submit is in flight (drives the shared
  // ProjectService write-back after the dialog confirms).
  private activeSubmitProduct: WorkspaceProduct | null = null;
  // Dialog-bound fields, populated from `activeSubmitProduct` on open so the
  // dialog reflects that product (address is shared).
  dialogDocuments: any[] = [];
  dialogStatus: string = '';
  dialogSiteType: string = '';
  dialogSiteSubType: string = '';
  dialogIsAlta: boolean = false;

  // --- Google map (parcel polygon) — same config as the single-site path ---
  center: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };
  zoom: number = 4;
  mapOptions: google.maps.MapOptions = {};
  polygonPaths: google.maps.LatLngLiteral[] = [];
  polygonOptions: google.maps.PolygonOptions = {
    fillColor: '#6296ff',
    fillOpacity: 0.25,
    strokeColor: '#537ed5',
    strokeOpacity: 1,
    strokeWeight: 3,
    clickable: false,
    editable: false,
    draggable: false,
  };
  mapsLoaded: boolean = false;

  private static readonly DAY_MS = 86_400_000;

  // Which product statuses belong in the workspace, per route. The /active
  // cockpit mirrors the Active tab (ACTIVE / REVIEW_PENDING / REVISION_REQUESTED
  // / REVISION_PENDING); the read-only /project-history view shows COMPLETED.
  // INVITATION (quote) and BID_WON (awarded, unsigned) live on Invitations/
  // Pending, so they're excluded here.
  private static readonly ACTIVE_PHASE = new Set<string>([
    'ACTIVE',
    'REVIEW_PENDING',
    'REVISION_REQUESTED',
    'REVISION_PENDING',
  ]);
  private static readonly HISTORY_PHASE = new Set<string>(['COMPLETED']);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private projectService: ProjectService,
    private utilityService: CommonUtilityServiceService,
    private proposalService: ProposalService,
    private toastr: ToastrService,
    private mapsLoader: GoogleMapsLoaderService,
    private datePipe: DatePipe,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.mapsLoadedSub = this.mapsLoader.loaded$.subscribe((loaded) => {
      this.mapsLoaded = loaded;
      if (loaded && this.representativeSiteDetail) {
        this.initMap();
      }
    });
  }

  // How this workspace was entered (set by the site-card Open Workspace
  // routerLink state) — drives the Back button's label + target. 'project-
  // overview' → came from Project Overview; otherwise the Active list (or
  // Project History when readOnly).
  private backOrigin: string | null = null;

  ngOnInit(): void {
    // Capture the entry origin from the routerLink navigation state (persisted
    // on history.state so it survives into ngOnInit). Null on a direct load /
    // email deep link — the Back button then falls back to the Active list.
    this.backOrigin =
      (window.history.state &&
        window.history.state['workspaceBackType']) ||
      null;

    // Route data carries the view-only flag for /project-history/:id (same
    // channel proposal-details reads `type` from). Absent on /active/:id.
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.readOnly = !!data['readOnly'];
    });
    // Same active data path as the single-site workspace: fetch the PA's
    // projects and (re)derive the physical site's product collection on every
    // emission. Re-deriving by `siteNumber` (not refiltering to one `site.id`)
    // is what keeps a per-product write from collapsing the local view to a
    // single product. `switchMap` swaps to a fresh ProjectService subscription
    // when the route `:id` changes (no stacked subscriptions), and `takeUntil`
    // ends it on destroy so a navigated-away workspace stops reacting to the
    // app-wide ReplaySubject.
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.id = params.get('id') || '';
          return this.projectService.fetchData();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => this.rebuildFromDetails(result));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mapsLoadedSub.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Aggregation — resolve the route `site.id` to its project + physical site,
  // gather all sibling products (reusing the Spec-4 grouping), and rebuild the
  // shared site-level fields + the per-product view-models.
  // ---------------------------------------------------------------------------
  private rebuildFromDetails(result: any): void {
    const sanitized = sanitizeProjectDetails(result);

    // Find the project that owns the route product, then group it into the
    // Project → Site → Product hierarchy and pick the site the route product
    // belongs to — its `products` are the physical site's full product set.
    const matchedProject = sanitized.find(
      (parent: any) =>
        parent.projectId &&
        parent.sites.some((child: any) => child?.site?.id === this.id)
    );
    if (!matchedProject) {
      this.matchedProject = null;
      this.products = [];
      this.representativeSiteDetail = null;
      return;
    }
    this.matchedProject = matchedProject;

    const projectVm = toProjectVm(matchedProject);
    const siteVm: SiteVm | undefined = projectVm.sites.find((s: SiteVm) =>
      s.products.some((p: ProductVm) => p.site?.id === this.id)
    );
    // Keep only the products that belong to THIS workspace's tab — the /active
    // cockpit shows Active-phase products, /project-history shows COMPLETED.
    // (A physical site's quote/awarded products stay on Invitations/Pending.)
    const workspacePhase = this.readOnly
      ? WorkspaceComponent.HISTORY_PHASE
      : WorkspaceComponent.ACTIVE_PHASE;
    const productVms: ProductVm[] = (siteVm ? siteVm.products : []).filter(
      (p: ProductVm) => workspacePhase.has(p.status)
    );
    if (!productVms.length) {
      this.products = [];
      this.representativeSiteDetail = null;
      return;
    }
    this.siteNumber = siteVm?.siteNumber ?? null;

    // Per-product-unique testids — one scope per render pass so a repeated
    // product slug (e.g. two subtypes of the same service) dedupes to `-2`/`-3`.
    const testIdScope = createTestIdScope();
    this.products = productVms.map((pvm) =>
      this.buildProduct(pvm, testIdScope)
    );

    // Representative row for the shared site-info block — prefer the deep-linked
    // product so map/geometry match the opened site.
    const linked =
      this.products.find((p) => p.site?.id === this.id) ?? this.products[0];
    this.representativeSiteDetail = linked.siteDetail;

    this.buildSharedSiteInfo(linked);
    this.buildRollup();

    if (this.mapsLoaded) {
      this.initMap();
    }
  }

  // Build one product's fully-derived view-model.
  private buildProduct(
    pvm: ProductVm,
    testIdScope: { next: (id: string) => string }
  ): WorkspaceProduct {
    const siteDetail = pvm.siteDetail;
    const site = pvm.site ?? {};
    const proposal = pvm.proposal ?? {};
    const status: string = proposal.status ?? '';
    const isAlta = site.serviceType === 'NEXUS_ALTA_SURVEY';

    // Merge site + proposal documents and derive submit gating exactly as the
    // single-site path did (defaults false when a proposal carries no docs).
    let documents: any[] = [];
    let showReviewSubmission = false;
    let showRevisionSubmission = false;
    if (site.documents) {
      documents = [...documents, ...site.documents];
    }
    if (proposal.documents) {
      documents = [...documents, ...proposal.documents];
      showReviewSubmission =
        !isAlta ||
        proposal.documents.some(
          (document: { type: string }) => document.type === 'DELIVERABLE'
        );
      showRevisionSubmission =
        !isAlta ||
        proposal.documents.some(
          (document: { type: string }) => document.type === 'REVISION'
        );
    }

    // The one site-visit status input, shared by the Details row below and by
    // the Field Work Scheduled action item further down. Upper-cased
    // defensively: the server serializes the enum name, but a lower-cased
    // value must not silently miss the set and read as "never scheduled".
    const siteVisitStatus =
      pvm.siteVisit?.status == null
        ? null
        : String(pvm.siteVisit.status).toUpperCase();

    // Details subcard "Site Visit" row — reads the `siteVisit` summary the feed
    // attaches to this product (`GET /project/user-projects`), not
    // `proposal.selectedSiteVisit`. That field is written only by the Order
    // Tracker staff flow (`saveSiteVisitTask`); CONNECT 2.0's own site-visit
    // negotiation writes a `SiteVisitSchedule` and never touches it, so a
    // new-flow visit rendered as never scheduled here. The date is the
    // summary's `confirmedDate` — never `confirmedTime`, a wall-clock string
    // with no time zone attached to it. 🔴 `timeWindow` DROPPED (Task 10)
    // along with the AM/PM window it labelled; the row shows the date alone.
    // Gated on `SITE_VISIT_SCHEDULED_STATUSES` — the same set the stepper
    // (`resolveProductProgress`) uses — so a cancelled or reopened schedule
    // (CANCELLED, REJECTED_ALTERNATIVES_PROVIDED,
    // PENDING_SITE_CONTACT_APPROVAL) does not keep showing its last agreed
    // day next to a stepper that has already regressed to "unscheduled".
    // The row is additionally gated on `site.siteVisitRequired` (see
    // `siteVisitRequired` below) — it does not render at all for a product that
    // needs no visit, so there is no "Not required" text to render either.
    const siteVisitScheduled =
      siteVisitStatus !== null &&
      SITE_VISIT_SCHEDULED_STATUSES.has(siteVisitStatus as SiteVisitStatus);
    const siteVisitSummaryDate = siteVisitScheduled
      ? this.toDate(pvm.siteVisit?.confirmedDate)
      : null;

    const isReviewStage = status === 'ACTIVE';
    const isRevisionStage = status === 'REVISION_REQUESTED';
    const isPendingState =
      status === 'REVIEW_PENDING' ||
      status === 'REVISION_PENDING' ||
      status === 'IN_REVIEW';
    const isComplete = status === 'COMPLETED';

    const dueDate = this.toDate(
      site.desiredByDate ?? proposal.proposedDeliverDate
    );
    const committedDate = this.toDate(proposal.proposedDeliverDate);
    const surveyDueDate =
      this.toDate(proposal.surveyDueDate ?? site.surveyDueDate) ?? dueDate;
    const approvedDelayDate = this.toDate(
      proposal.approvedDelayDueDate ?? site.approvedDelayDueDate
    );
    const effectiveDueDate = approvedDelayDate ?? surveyDueDate;

    // Field Work Scheduled action item — reads the `siteVisit` summary the feed
    // attaches to this product (`GET /project/user-projects`), not
    // `proposal.selectedSiteVisit`. That field is written only by the Order
    // Tracker staff flow (`saveSiteVisitTask`); CONNECT 2.0's own site-visit
    // negotiation writes a `SiteVisitSchedule` and never touches it, so a
    // new-flow visit rendered a permanent outstanding ✗ here. `done` is the
    // explicit `SITE_VISIT_COMPLETED` signal — no date proxy. The displayed
    // date is the summary's `confirmedDate`; never `confirmedTime`, a wall-clock
    // string with no time zone attached to it. The row is gated on
    // `site.siteVisitRequired` (see `siteVisitRequired` below): a product that
    // needs no site visit shows no field-work checklist row at all.
    const fieldWorkDone = siteVisitStatus === 'SITE_VISIT_COMPLETED';
    const fieldWorkItem: ActionItemState = {
      done: fieldWorkDone,
      date: fieldWorkDone ? this.toDate(pvm.siteVisit?.confirmedDate) : null,
    };
    const prelimDoc = (proposal.documents ?? []).find(
      (document: any) => document.type === 'PRELIMINARY'
    );
    const prelimSurveyItem: ActionItemState = {
      done: !!prelimDoc,
      date: prelimDoc ? this.documentDate(prelimDoc) : null,
    };
    const revisions = documents.filter(
      (document: any) => document.type === 'REVISION'
    );
    const latestRevision = revisions.length
      ? revisions[revisions.length - 1]
      : null;
    const revisedSurveyItem: ActionItemState = {
      done: !!latestRevision,
      date: latestRevision ? this.documentDate(latestRevision) : null,
    };

    const outstandingCommentsCount =
      proposal.outstandingComments ?? site.outstandingComments ?? 0;

    const banner = isAlta
      ? this.computeStatus({
          isComplete,
          submitted: isPendingState,
          revisionRequested: isRevisionStage,
          outstanding: outstandingCommentsCount,
          due: effectiveDueDate,
          delivered: revisedSurveyItem.done,
          noun: 'survey',
          pastDueCta: 'upload the latest revision',
        })
      : this.computeStatus({
          isComplete,
          submitted: isPendingState,
          revisionRequested: isRevisionStage,
          outstanding: outstandingCommentsCount,
          due: dueDate,
          // Owed by the vendor while ACTIVE *or* REVISION_REQUESTED — both are
          // pre-submission windows, so the due-date (past-due/due-soon) banner
          // applies to a requested revision too.
          delivered: !isReviewStage && !isRevisionStage,
          noun: 'deliverable',
          pastDueCta: 'submit your deliverable',
        });

    const reportWriterUrl: string | null = proposal.reportWriterUrl ?? null;

    return {
      siteDetail,
      site,
      proposal,
      status,
      isAlta,
      title: buildProductTitle(site),
      testId: buildTestId(testIdScope, buildProductTitle(site) || 'product'),

      stageIndex: this.stageIndexFor(status),
      statusPillKey: this.statusPillKeyFor(status),
      statusPillLabel: this.statusPillLabelFor(status),

      banner,
      // Every product leads with a status banner in every post-award state
      // (parity across survey + report): Completed / Past Due / Due Soon /
      // Revision Requested / Submitted — In Review / On Track. `computeStatus`
      // always resolves a meaningful state, so there is no blank case to hide.
      showBanner: true,
      isPastDue: banner.key === 'past-due',

      isReviewStage,
      isRevisionStage,
      isPendingState,
      isComplete,
      pendingLabel:
        status === 'REVISION_PENDING' ? 'Revision Pending' : 'Review Pending',
      showReviewSubmission,
      showRevisionSubmission,

      documents,

      reportWriterUrl,
      hasReportWriterLink: !!reportWriterUrl,

      dueDate,
      committedDate,
      reportId:
        proposal.reportId ??
        site.reportId ??
        site.externalOrderNumber ??
        '—',
      staffingCoordinator: this.resolveContact(
        site.staffingCoordinator ?? proposal.staffingCoordinator
      ),
      seniorReviewer: this.resolveContact(
        site.seniorReviewer ?? proposal.seniorReviewer
      ),
      siteVisitRequired: site.siteVisitRequired === true,
      siteVisitSummary: this.buildSiteVisitSummary(siteVisitSummaryDate),

      authorizationDate: this.toDate(
        proposal.authorizationDate ?? site.authorizationDate
      ),
      businessDaysQuoted:
        proposal.businessDaysQuoted ?? site.businessDaysQuoted ?? null,
      surveyDueDate,
      titleWorkProvidedDate: this.toDate(
        proposal.titleWorkProvidedAt ?? site.titleWorkProvidedAt
      ),
      approvedDelayDate,
      outstandingCommentsCount,

      fieldWorkItem,
      prelimSurveyItem,
      revisedSurveyItem,
    };
  }

  // Shared site-level fields — read off the representative product (identical
  // across the group). All defensive: absent fields degrade to hidden/empty.
  private buildSharedSiteInfo(linked: WorkspaceProduct): void {
    const siteDetail = linked.siteDetail;
    const site = linked.site ?? {};

    this.address = this.utilityService.formatAddress(siteDetail);
    this.siteAcreage = site.totalAcreage ?? null;
    this.projectManager = this.resolveContact(
      site.projectManager ?? linked.proposal?.projectManager
    );

    const items = site.tableA;
    if (items?.length) {
      const containsItem21 = items.some((item: any) => item.itemName === '21');
      const response = this.utilityService.formatTableA(
        siteDetail,
        containsItem21
      );
      this.tableAText = response ? response[1] : '';
      // Index 0 is the Item 20/21 free text and index 2 names which item it came
      // from. Dropping them here was the workspace half of the same defect the
      // list had: a client types a requirement into that field and the PA doing
      // the work never sees it.
      this.tableAItemText = response ? response[0] : '';
      this.tableAItemLabel = response ? response[2] : '';
    } else {
      this.tableAText = '';
      this.tableAItemText = '';
      this.tableAItemLabel = '';
    }
    this.tableAItemsAdded = site.tableAChanges?.itemsAdded ?? '';
    this.tableAItemsRemoved = site.tableAChanges?.itemsRemoved ?? '';
    this.hasTableAChanges = !!(this.tableAItemsAdded || this.tableAItemsRemoved);

    this.specialSiteCondition = site.specialSiteCondition ?? '';
    this.siteDetailsText = site.siteDetails ?? '';
    this.siteVisitInstructions = site.siteVisitInstructions ?? '';

    // Shared received docs — aggregate + de-dup ACROSS ALL of the site's
    // products, not just the representative. Reading one representative dropped
    // siblings' received docs from view: PROPOSAL docs are per-proposal, and
    // SURVEY_CHECKLIST lives only on the survey product — so a report deep-link
    // (representative = a report) would hide the survey checklist and every
    // sibling proposal's docs, and the shared set would change depending on
    // which product was clicked to enter. De-dup by id (falling back to
    // fileName|type) so replicated SITE/ATP docs still show once. The
    // documents-card's `receivedDocuments` getter filters this merged list down
    // to the SITE/ATP/PROPOSAL/SURVEY_CHECKLIST subset.
    const seenDocKeys = new Set<string>();
    this.sharedDocuments = this.products.reduce((acc: any[], product) => {
      for (const doc of product.documents ?? []) {
        const key = doc?.id ?? `${doc?.fileName ?? ''}|${doc?.type ?? ''}`;
        if (!seenDocKeys.has(key)) {
          seenDocKeys.add(key);
          acc.push(doc);
        }
      }
      return acc;
    }, []);
  }

  // Rollup pill + per-status counts across the site's products.
  //   pill = Action Needed (danger) if ANY product is past due OR has a
  //          revision requested (both are vendor-action states)
  //        · Completed (success) iff EVERY product is COMPLETED
  //        · In Progress (info) otherwise
  // Counts a revision-requested product in its own bucket (not "Active" or
  // "In Review") so the breakdown agrees with its "Revision Requested" pill.
  private buildRollup(): void {
    const counts: StatusCounts = {
      pastDue: 0,
      revisionRequested: 0,
      inReview: 0,
      active: 0,
      complete: 0,
    };
    for (const product of this.products) {
      if (product.isPastDue) {
        counts.pastDue++;
      } else if (product.isComplete) {
        counts.complete++;
      } else if (product.isRevisionStage) {
        counts.revisionRequested++;
      } else if (product.isPendingState) {
        counts.inReview++;
      } else {
        counts.active++;
      }
    }
    this.counts = counts;

    const everyComplete =
      this.products.length > 0 &&
      this.products.every((product) => product.isComplete);
    if (counts.pastDue > 0 || counts.revisionRequested > 0) {
      this.rollupPillKey = 'not_awarded'; // danger
      this.rollupPillLabel = 'Action Needed';
    } else if (everyComplete) {
      this.rollupPillKey = 'awarded'; // success
      this.rollupPillLabel = 'Completed';
    } else {
      this.rollupPillKey = 'in_progress'; // info/active
      this.rollupPillLabel = 'In Progress';
    }
  }

  // Sections — Survey (0–1) and Reports & Assessments (0–N).
  get surveyProducts(): WorkspaceProduct[] {
    return this.products.filter((product) => product.isAlta);
  }

  get reportProducts(): WorkspaceProduct[] {
    return this.products.filter((product) => !product.isAlta);
  }

  private initMap(): void {
    if (!this.representativeSiteDetail) {
      return;
    }
    this.mapOptions = {
      mapTypeId: google.maps.MapTypeId.HYBRID,
      mapTypeControl: false,
    };
    this.center = this.utilityService.determineMapCenter(
      this.representativeSiteDetail.site.parcels
    );
    this.zoom = this.utilityService.determineMapZoomLevel(
      this.representativeSiteDetail.site.totalAcreage
    );
    this.polygonPaths = this.utilityService.detemineMapBounderies(
      this.representativeSiteDetail
    );
  }

  // Back button label reflects where the vendor came from — so a workspace
  // opened from Project Overview doesn't read as "Back to Active".
  get backLabel(): string {
    if (this.readOnly) {
      return 'Back to Project History';
    }
    return this.backOrigin === 'project-overview'
      ? 'Back to Project Overview'
      : 'Back to Active';
  }

  // From Project Overview → return there explicitly (it may have been reached
  // by an email deep link with no in-app history to pop). Otherwise POP-navigate
  // so the app-wide scroll-restoration config restores the list's scroll
  // position (Active list, or Project History when readOnly).
  back(): void {
    if (
      !this.readOnly &&
      this.backOrigin === 'project-overview' &&
      this.matchedProject?.projectId
    ) {
      this.router.navigate([
        '/project-overview',
        this.matchedProject.projectId,
      ]);
      return;
    }
    // Entered from the Dashboard work-map pin: there is no Active list in the
    // history to pop back to (location.back() would land on the Dashboard), so
    // navigate to the Active list explicitly — matching the "Back to Active"
    // label. (Entering from the Active list keeps location.back() below, which
    // also restores that list's scroll position.)
    if (!this.readOnly && this.backOrigin === 'dashboard') {
      this.router.navigate(['/active']);
      return;
    }
    this.location.back();
  }

  // ---------------------------------------------------------------------------
  // Pure per-status derivations (were single-site getters; now keyed by status
  // so each product resolves independently).
  // ---------------------------------------------------------------------------
  private stageIndexFor(status: string): number {
    switch (status) {
      case 'COMPLETED':
        return 2;
      case 'IN_REVIEW':
      case 'REVIEW_PENDING':
      case 'REVISION_REQUESTED':
      case 'REVISION_PENDING':
        return 1;
      default:
        return 0;
    }
  }

  private statusPillKeyFor(status: string): string {
    switch (status) {
      case 'COMPLETED':
        return 'awarded';
      // Revision requested = the vendor owes an updated deliverable (action
      // needed), distinct from the with-the-team "In Review" states below.
      case 'REVISION_REQUESTED':
        return 'pending';
      case 'IN_REVIEW':
      case 'REVIEW_PENDING':
      case 'REVISION_PENDING':
        return 'pending';
      default:
        return 'in_progress';
    }
  }

  private statusPillLabelFor(status: string): string {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      // Owed by the vendor — labelled distinctly from the with-the-team
      // "In Review" states so the pill doesn't imply "no action needed".
      case 'REVISION_REQUESTED':
        return 'Revision Requested';
      case 'IN_REVIEW':
      case 'REVIEW_PENDING':
      case 'REVISION_PENDING':
        return 'In Review';
      default:
        return 'Authorized';
    }
  }

  private toDate(raw: any): Date | null {
    return raw ? new Date(raw) : null;
  }

  private resolveContact(raw: any): WorkspaceContact {
    if (!raw) {
      return { name: null, email: null };
    }
    if (typeof raw === 'string') {
      return raw.includes('@')
        ? { name: raw, email: raw }
        : { name: raw, email: null };
    }
    return {
      name: raw.name ?? raw.fullName ?? raw.email ?? null,
      email: raw.email ?? null,
    };
  }

  // Renders the Details subcard's "Site Visit" row. No "not required" case: the
  // row itself is gated on `siteVisitRequired`, so this is only ever called for
  // a product that does need a visit. 🔴 The AM/PM window it used to append in
  // parentheses is gone (Task 10) along with `SiteVisitSummary.timeWindow`; the
  // summary is the date alone.
  private buildSiteVisitSummary(date: Date | null): string {
    if (date) {
      return new Date(date).toLocaleDateString();
    }
    return 'Not yet scheduled';
  }

  private documentDate(doc: any): Date | null {
    const raw =
      doc?.createdDate ?? doc?.uploadedDate ?? doc?.uploadDate ?? doc?.date;
    return raw ? new Date(raw) : null;
  }

  // Renders an Action Items row's trailing text — "Completed {date}" (date
  // omitted when unknown) or "Outstanding".
  actionItemStatusText(item: ActionItemState): string {
    if (!item.done) {
      return 'Outstanding';
    }
    const formatted = item.date
      ? this.datePipe.transform(item.date, 'MM/dd/yyyy')
      : null;
    return formatted ? `Completed ${formatted}` : 'Completed';
  }

  // Status banner — mirrors connect-ws `deriveSiteStatus`: a completed order
  // short-circuits to a positive banner; then outstanding comments; then
  // due-date proximity (only while the deliverable is still owed); then, for a
  // product already submitted and awaiting review, the 'in-review' state; else
  // on-track. Pure over the passed config so each product resolves its own
  // banner.
  private computeStatus(cfg: {
    isComplete: boolean;
    submitted: boolean;
    revisionRequested: boolean;
    outstanding: number;
    due: Date | null;
    delivered: boolean;
    noun: string;
    pastDueCta: string;
  }): AltaStatus {
    if (cfg.isComplete) {
      return {
        key: 'completed',
        triad: 'success',
        icon: 'check_circle',
        label: 'Completed',
        message: `This order is complete. No further action is needed on this ${cfg.noun}.`,
      };
    }

    if (cfg.outstanding > 0) {
      return {
        key: 'outstanding-comments',
        triad: 'info',
        icon: 'chat',
        label: 'Outstanding Comments',
        message: `${cfg.outstanding} review comment${
          cfg.outstanding === 1 ? '' : 's'
        } ${cfg.outstanding === 1 ? 'is' : 'are'} awaiting your response.`,
      };
    }

    const { due, delivered } = cfg;
    if (due) {
      const diffDays = Math.ceil(
        (due.getTime() - Date.now()) / WorkspaceComponent.DAY_MS
      );
      if (diffDays < 0 && !delivered) {
        const overdue = Math.abs(diffDays);
        return {
          key: 'past-due',
          triad: 'danger',
          icon: 'error',
          label: 'Past Due',
          message: `This ${cfg.noun} is ${overdue} day${
            overdue === 1 ? '' : 's'
          } past the due date. Please ${cfg.pastDueCta} or notify us of a delay.`,
        };
      }
      if (diffDays >= 0 && diffDays <= 5 && !delivered) {
        return {
          key: 'due-soon',
          triad: 'warning',
          icon: 'schedule',
          label: 'Due Soon',
          message: `Due in ${diffDays} day${
            diffDays === 1 ? '' : 's'
          }. Please keep your deliverables on schedule.`,
        };
      }
    }

    // A revision was explicitly requested — an action-needed state even when
    // not (yet) past due. Ranks below past-due/due-soon (those are more urgent
    // and already fired above when the revision is overdue) but above the
    // on-track fallback, so a requested revision never reads as "On Track".
    if (cfg.revisionRequested) {
      return {
        key: 'revision-requested',
        triad: 'warning',
        icon: 'assignment_return',
        label: 'Revision Requested',
        message: `A revision has been requested. Please submit your updated ${cfg.noun}.`,
      };
    }

    // The deliverable has been submitted (past ACTIVE) but is not yet complete —
    // it is with the review team. Takes precedence over the on-track fallback so
    // a submitted product never reads as still-owed or blank.
    if (cfg.submitted) {
      return {
        key: 'in-review',
        triad: 'info',
        icon: 'hourglass_top',
        label: 'Submitted — In Review',
        message: `Your ${cfg.noun} is with the CREtelligent review team. No action needed unless a revision is requested.`,
      };
    }

    return {
      key: 'on-track',
      triad: 'success',
      icon: 'check_circle',
      label: 'On Track',
      message: `The ${cfg.noun} is currently on schedule and no urgent issues are flagged at this time.`,
    };
  }

  // ---------------------------------------------------------------------------
  // Deliverable submit — opens the shared confirmation dialog scoped to one
  // product; the dialog emits back to submitReview()/submitRevision(), which
  // call ProposalService for THAT product's proposal (N independent calls;
  // Task 2 refines per-product deliverable behavior).
  // ---------------------------------------------------------------------------
  openSubmitForReview(product: WorkspaceProduct): void {
    this.activeSubmitProduct = product;
    this.dialogDocuments = product.documents;
    this.dialogStatus = product.status;
    this.dialogSiteType = product.site?.siteType ?? '';
    this.dialogSiteSubType = product.site?.siteSubType ?? '';
    this.dialogIsAlta = product.isAlta;
    this.submitForReviewActive = true;
  }

  toggleSubmitForReviewModal(): void {
    this.submitForReviewActive = !this.submitForReviewActive;
  }

  submitReview(): void {
    const product = this.activeSubmitProduct;
    if (!product) {
      return;
    }
    this.proposalService
      .submitReadyForReview(product.proposal.id)
      .subscribe((response: any) =>
        this.applyProposalResponse(product, response)
      );
  }

  submitRevision(): void {
    const product = this.activeSubmitProduct;
    if (!product) {
      return;
    }
    this.proposalService
      .submitRevision(product.proposal.id)
      .subscribe((response: any) =>
        this.applyProposalResponse(product, response)
      );
  }

  private applyProposalResponse(
    product: WorkspaceProduct,
    response: any
  ): void {
    if (!response || response.status === 'FAILURE') {
      this.toastr.error(
        response?.message || 'An error occurred. Please try again.'
      );
      return;
    }
    const updatedProposal = response.data;
    if (!updatedProposal) {
      this.toastr.error('An error occurred. Please try again.');
      return;
    }
    this.mergeSiteBack({ ...product.siteDetail, proposal: updatedProposal });
  }

  // ---------------------------------------------------------------------------
  // Child-card write-back — Documents / Scheduling / Comments each emit the
  // updated `{ siteDetail }` for their OWN product. Merge it into the shared
  // ProjectService list keyed by `site.id` (siblings preserved), which re-emits
  // and re-derives the full product collection by `siteNumber`.
  // ---------------------------------------------------------------------------
  onProductUpdate(event: { siteDetail: any }): void {
    if (event?.siteDetail) {
      this.mergeSiteBack(event.siteDetail);
    }
  }

  private mergeSiteBack(updatedSiteDetail: any): void {
    if (!this.matchedProject) {
      return;
    }
    const updatedProjectDetail = {
      ...this.matchedProject,
      sites: [updatedSiteDetail],
    };
    this.projectService.updateProjectDataSubjectWithSingleSite(
      updatedProjectDetail
    );
  }
}
